import type { Logger } from '@vp/ubik-logging'

import type { CartResponse } from '../types/VistaCart'
import { getCountryFromLocale } from '../utils/LocalizationHelpers'
import { REQUESTOR } from '../utils/constants'

const baseUrl = 'https://vistacart.orders.vpsvc.com'
const TIMEOUT_MS = 10000

export const buildCartTenant = (culture: string): string => {
  const countryCode = getCountryFromLocale(culture).toUpperCase()
  return `VP-${countryCode}-PROD`
}

export const getCart = async (culture: string, shopperId: string, authorizationHeader: string, logger: Logger): Promise<CartResponse> => {
  const startTime = Date.now()
  const cartTenant = buildCartTenant(culture)
  const url = `${baseUrl}/tenants/${cartTenant}/carts/${shopperId}?requestor=${REQUESTOR}`

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS)

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: authorizationHeader,
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      throw new Error(`Request to ${url} failed with status: ${response.status}`)
    }

    const data = (await response.json()) as CartResponse
    return data
  } catch (error) {
    clearTimeout(timeoutId)
    const duration = Date.now() - startTime

    if (error instanceof Error && error.name === 'AbortError') {
      logger.error('VistaCart request timed out', error, { url, culture, duration, timeout: TIMEOUT_MS })
    } else {
      logger.error('Failed to fetch cart from VistaCart', error as Error, { url, culture, duration })
    }

    throw error
  }
}
